// https://support.axon.ai/en/growth/promoting-your-websites/axon-pixel-integration/events-and-objects/
// Cache bust: 2026-03-14

import { cartItemToSegmentProducts, ProductEventProps } from '../lib/analytics/events';
import { centsToDollarsNumber } from '../lib/util';
import { CartItem, CartPricing } from './carts';

// Axon category ID: https://support.axon.ai/en/growth/promoting-your-websites/axon-pixel-integration/events-and-objects/#category-ids
// Pet Collars & Harnesses (6250) is the most specific category for Fi smart dog collars
const AXON_CATEGORY_PET_COLLARS_HARNESSES = 6250;

export interface AxonUserData {
  email?: string;
  phone?: string;
  user_id?: string;
}

export interface AxonTrack {
  (eventType: 'track', eventName: 'page_view', data?: { user_data?: AxonUserData }): void;
  (
    eventType: 'track',
    eventName: 'view_cart',
    data: { currency: string; items: AxonItem[]; value?: number; user_data?: AxonUserData },
  ): void;
  (
    eventType: 'track',
    eventName: 'view_item',
    data: { currency: string; items: AxonItem[]; value?: number; user_data?: AxonUserData },
  ): void;
  (
    eventType: 'track',
    eventName: 'add_to_cart',
    data: { currency: string; items: AxonItem[]; value?: number; user_data?: AxonUserData },
  ): void;
  (
    eventType: 'track',
    eventName: 'begin_checkout',
    data: { currency: string; items: AxonItem[]; value: number; user_data?: AxonUserData },
  ): void;
  (
    eventType: 'track',
    eventName: 'purchase',
    data: {
      currency: string;
      items: AxonItem[];
      transaction_id: string;
      value: number;
      shipping?: number;
      tax?: number;
      user_data?: AxonUserData;
    },
  ): void;
  (
    eventType: 'track',
    eventName: 'add_payment_info',
    data: {
      currency: string;
      items?: AxonItem[];
      payment_type?: AxonPaymentType;
      value?: number;
      user_data?: AxonUserData;
    },
  ): void;
  (
    eventType: 'track',
    eventName: 'generate_lead',
    data: { currency?: string; value?: number; user_data?: AxonUserData },
  ): void;
  (eventType: 'track', eventName: 'login', data: { user_data?: AxonUserData }): void;
  (eventType: 'track', eventName: 'sign_up', data: { method?: string; user_data?: AxonUserData }): void;
  (
    eventType: 'track',
    eventName: 'search',
    data: { search_term: string; results?: AxonItem[]; user_data?: AxonUserData },
  ): void;
  (
    eventType: 'track',
    eventName: 'subscribe',
    data: { currency?: string; value?: number; user_data?: AxonUserData },
  ): void;
  (
    eventType: 'track',
    eventName: 'remove_from_cart',
    data: { currency: string; items: AxonItem[]; value?: number; user_data?: AxonUserData },
  ): void;
}

interface AxonItem {
  item_variant_id?: string;
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  image_url?: string;
  item_category_id?: number; // https://support.axon.ai/en/growth/promoting-your-websites/axon-pixel-integration/events-and-objects/#category-ids
  affiliation?: string;
  discount?: number;
  item_brand?: string;
}

type AxonPaymentType = 'credit_card' | 'deferred' | 'redeemable' | 'payment_on_delivery' | 'wallet' | 'other';

export function productEventPropsToAxonItem(props: ProductEventProps & { variant?: string }): AxonItem {
  return {
    item_id: props.sku,
    item_name: props.productName,
    price: centsToDollarsNumber(props.priceInCents), // Axon expects a price in dollars, not cents
    quantity: props.quantity ?? 1,
    item_variant_id: props.variant,
    item_category_id: AXON_CATEGORY_PET_COLLARS_HARNESSES,
    item_brand: 'Fi',
  };
}

export function cartItemToAxonItems(cartItem: CartItem, currency: string): AxonItem[] {
  const axonItems: AxonItem[] = [];

  const segmentProducts = cartItemToSegmentProducts(cartItem, currency);

  for (const segmentProduct of segmentProducts) {
    axonItems.push({
      item_id: segmentProduct.product_id,
      price: segmentProduct.price,
      quantity: segmentProduct.quantity,
      item_name: segmentProduct.name,
      item_variant_id: segmentProduct.variant,
      item_category_id: AXON_CATEGORY_PET_COLLARS_HARNESSES,
      item_brand: 'Fi',
    });
  }

  return axonItems;
}

export function maybeAddAxonSetupFeeItem(cartPricing: CartPricing, axonItems: AxonItem[]): AxonItem[] {
  if (cartPricing.activationFeesInCents && cartPricing.activationFeesInCents > 0) {
    axonItems.push({
      item_id: 'setup_fee',
      item_name: 'Setup Fee',
      price: centsToDollarsNumber(cartPricing.activationFeesInCents),
      quantity: 1,
    });
  }

  return axonItems;
}
